import { PRODUCT_TYPE } from '../constants/productType'

export interface ProductTypeProviderValuesInput {
  isLyraSelectEnabled: boolean
  isWellbeingEnabled: boolean
  productType: PRODUCT_TYPE
  isUserLoggedIn?: boolean
  lastLoggedInUserProductType?: PRODUCT_TYPE
}

export interface ProductTypeProviderValues {
  isLyraSelect: boolean
  isWellbeing: boolean
  productType: PRODUCT_TYPE
  isAuroraEnabled: boolean
}

/**
 * Derives the product theme context from the backend product type and product rollout flags.
 *
 * WFMH is the default product experience. Aurora is not a standalone product type; it is the
 * default experience for WFMH and Wellbeing.
 * Lyra Select owns its product experience and theme decisions, so it suppresses Aurora.
 * Lyra Select has its own Select-specific theme, microsite, and registration experience;
 * Wellbeing follows the same Aurora experience as WFMH.
 *
 * We preserve the existing product context gate for Lyra Select and also expose Wellbeing when its
 * rollout flag is enabled. Logged-out flows use the last logged-in product type for product
 * context, while the current backend product type still controls Select's existing Aurora
 * exclusion.
 */
export const getProductTypeProviderValues = ({
  isLyraSelectEnabled,
  isWellbeingEnabled,
  isUserLoggedIn = true,
  productType,
  lastLoggedInUserProductType,
}: ProductTypeProviderValuesInput): ProductTypeProviderValues => {
  const isLyraSelect = isLyraSelectEnabled && productType === PRODUCT_TYPE.SELECT
  const isWellbeing = isWellbeingEnabled && productType === PRODUCT_TYPE.WELLBEING
  const activeProductType = isUserLoggedIn ? productType : lastLoggedInUserProductType
  const shouldExposeProductContext =
    activeProductType === PRODUCT_TYPE.WELLBEING ? isWellbeingEnabled : isLyraSelectEnabled

  return {
    isLyraSelect,
    isWellbeing,
    // Preserve the existing product context gate and let Wellbeing use its own rollout flag.
    productType: shouldExposeProductContext && activeProductType ? activeProductType : PRODUCT_TYPE.WFMH,
    isAuroraEnabled: !isLyraSelect,
  }
}
